import React from 'react'
import { Link } from 'react-router-dom'

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Navigation */}
      <nav className="sticky top-0 bg-white/95 backdrop-blur shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                NeuroTrain
              </span>
            </div>
            <div className="flex space-x-4">
              <Link to="/terms" className="text-gray-700 hover:text-emerald-600 transition">
                Terms & Conditions
              </Link>
              <Link to="/" className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition">
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <section className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h1 className="text-3xl font-bold mb-6 text-gray-900">Privacy Policy</h1>
          <p className="text-sm text-gray-600 mb-6">Effective Date: January 1, 2025 | Last Updated: January 1, 2025</p>
          
          <div className="space-y-6 text-gray-700">
            <div>
              <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
              <p className="mb-4">
                PATAGON CONSULTING INC., doing business as HELLO PROJECTS PRO ("we," "us," or "our"), operates the NeuroTrain 
                call analysis platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                in compliance with the California Consumer Privacy Act (CCPA) and other applicable privacy laws.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
              <p className="mb-4"><strong>2.1 Personal Information:</strong></p>
              <ul className="list-disc pl-6 mb-4">
                <li>Name and contact information (email, phone number)</li>
                <li>Account credentials</li>
                <li>Payment information (processed by Stripe)</li>
                <li>Business information</li>
              </ul>
              
              <p className="mb-4"><strong>2.2 Call Data:</strong></p>
              <ul className="list-disc pl-6 mb-4">
                <li>Audio recordings uploaded for analysis</li>
                <li>Transcriptions generated from audio</li>
                <li>Analysis results and performance metrics</li>
                <li>Agent names and identifiers</li>
              </ul>

              <p className="mb-4"><strong>2.3 Usage Data:</strong></p>
              <ul className="list-disc pl-6 mb-4">
                <li>IP addresses</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent</li>
                <li>Click patterns and feature usage</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">3. California Consumer Privacy Act (CCPA) Rights</h2>
              <p className="mb-4">
                If you are a California resident, you have the following rights under the CCPA:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Right to Know:</strong> You may request information about the personal information we collect, use, and disclose</li>
                <li><strong>Right to Delete:</strong> You may request deletion of your personal information</li>
                <li><strong>Right to Opt-Out:</strong> You may opt-out of the sale of personal information (Note: We do not sell personal information)</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
              </ul>
              <p className="mb-4">
                To exercise these rights, contact us at hello@patagonusa.com or call (888) 706-0080.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">4. How We Use Your Information</h2>
              <ul className="list-disc pl-6 mb-4">
                <li>Provide and maintain our Service</li>
                <li>Process transactions and billing</li>
                <li>Send administrative communications</li>
                <li>Improve and develop new features</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraud and security threats</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">5. Information Sharing</h2>
              <p className="mb-4">
                We do not sell, trade, or rent your personal information. We may share information with:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Service Providers:</strong> Third parties that help us operate our Service (e.g., Stripe for payments, OpenAI for transcription)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                <li><strong>Consent:</strong> With your explicit consent</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">6. Data Security</h2>
              <p className="mb-4">
                We implement appropriate technical and organizational measures to protect your information:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication protocols</li>
                <li>Regular security audits and updates</li>
                <li>Limited access to personal information</li>
                <li>Secure third-party payment processing (Stripe)</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">7. Data Retention</h2>
              <p className="mb-4">
                We retain personal information for as long as necessary to provide our Service and comply with legal obligations:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Account information: Duration of account plus 90 days</li>
                <li>Call recordings and transcriptions: 90 days unless deleted by user</li>
                <li>Analytics data: 1 year</li>
                <li>Legal compliance records: As required by law</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">8. Children's Privacy</h2>
              <p className="mb-4">
                Our Service is not intended for children under 18. We do not knowingly collect personal information from children. 
                If we discover that a child has provided us with personal information, we will delete it immediately.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">9. Cookies and Tracking</h2>
              <p className="mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Maintain user sessions</li>
                <li>Remember user preferences</li>
                <li>Analyze usage patterns</li>
                <li>Improve Service performance</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">10. Do Not Track Signals</h2>
              <p className="mb-4">
                We honor Do Not Track (DNT) browser signals. When DNT is enabled, we do not track your activity across websites 
                or collect analytics data.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3">11. Contact Us</h2>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <strong>PATAGON CONSULTING INC.</strong><br />
                Attn: Privacy Officer<br />
                18546 Sherman Way Ste 103<br />
                Reseda, CA 91335<br />
                Email: hello@patagonusa.com<br />
                Phone: (888) 706-0080
              </div>
              <p className="mb-4">
                <strong>Response Time:</strong> We will respond to privacy requests within 45 days.
              </p>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Your California Privacy Rights at a Glance:</h3>
              <ul className="list-disc pl-6">
                <li>Access your personal information</li>
                <li>Delete your personal information</li>
                <li>Opt-out of data sales (we don't sell data)</li>
                <li>Non-discrimination for exercising rights</li>
                <li>Designate an authorized agent</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, call us toll-free at <strong>(888) 706-0080</strong> or 
                email <strong> hello@patagonusa.com</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400 mb-2">© 2025 PATAGON CONSULTING INC. All rights reserved.</p>
          <p className="text-gray-400 text-sm">
            DBA Hello Projects Pro | 18546 Sherman Way Ste 103, Reseda, CA 91335
          </p>
        </div>
      </footer>
    </div>
  )
}

export default PrivacyPage
